// =====================================================================
// SALÉSOL — Signatures Book Pages
// =====================================================================
// Three house-made drinks: Velvet Chocolate, Ceremonial Matcha, Mont Blanc.
// Each renders via the `signature-drink` page kind in its own tonal world.
//
// Schema (per drink):
//   slug                — URL slug (e.g. 'velvet')
//   folio               — page-number stamp bottom-right ('04', '05', '06')
//   section             — top-right section marker (full uppercase name)
//   eyebrow             — small caps eyebrow above the headline
//   headline            — first word (roman) of headline
//   headlineItalic      — second word (italic accent in palette.accent color)
//   lede                — italic intro paragraph (3–4 lines, poetic)
//   illustrationType    — 'process-4step' | 'hero-single'
//   illustrationSteps   — for process-4step: array of 4 {roman, label, sub}
//   illustrationSrc     — optional image path (falls back to dashed placeholder)
//   plateNumber         — '4.1', '4.2', etc. — editorial reference code
//   plateRef            — secondary reference (e.g. 'A3') shown top-right of plate
//   plateCaption        — array of 1–2 caption lines (mono caps)
//   photoCaption        — small italic caption beneath polaroid (hero-single)
//   service             — array of price rows {name, descriptor, price}
//   footnote            — array of small italic lines (allergens, notes)
//   pagemarkSection     — bottom pagemark section text
//   pagemarkItalic      — bottom pagemark italic right text
//   palette             — full per-drink color palette (see PALETTES below)
// =====================================================================

// PALETTES — each drink gets its own tonal world.
// Velvet = warm cream + rust (chocolate tonality)
// Matcha = deep teal + cream-gold (Kyoto/jade tonality)
// Mont Blanc = champagne cream + amber (yuzu tonality)

const VELVET_PALETTE = {
  bg: '#EFE7D5',
  text: '#1A1410',
  textMuted: 'rgba(26, 20, 16, 0.62)',
  accent: '#A85328',
  accentSoft: '#8E4422',
  rule: 'rgba(26, 20, 16, 0.16)',
  plateBg: '#1A1410',
  plateText: '#EFE7D5',
};

const MATCHA_PALETTE = {
  bg: '#0F5049',          // brighter, more saturated cyan-teal — matches latest mockup
  text: '#F5F3EB',
  textMuted: 'rgba(245, 243, 235, 0.72)',
  accent: '#D4C4A6',
  accentSoft: '#A89770',
  rule: 'rgba(245, 243, 235, 0.18)',
  plateBg: '#0A3D38',
  plateText: '#D4C4A6',
};

const MONTBLANC_PALETTE = {
  bg: '#F5EFE3',
  text: '#2A1B14',
  textMuted: 'rgba(42, 27, 20, 0.62)',
  accent: '#C8924A',
  accentSoft: '#A87530',
  rule: 'rgba(42, 27, 20, 0.16)',
  plateBg: '#2A1B14',
  plateText: '#F5EFE3',
};

export const signaturePages = [
  // No slipcase/front-cover — customer lands directly on Velvet to
  // reduce taps-to-content (set 2026-05).

  // -------------------------------------------------------------------
  // i. VELVET CHOCOLATE  — warm cream + rust  (landing page, slug: '')
  // -------------------------------------------------------------------
  {
    slug: '',
    kind: 'signature-drink',
    folio: '01',
    section: 'I. SIGNATURES · VELVET CHOCOLATE',
    eyebrow: 'A house signature',
    headline: 'Velvet',
    headlineItalic: 'Chocolate',
    lede: 'Callebaut 70% dark couverture, melted with controlled heat and crafted into our house ganache. Folded into steamed milk.',
    illustrationType: 'hero-process',
    illustrationSrc: '/illustrations/velvet-hero.jpg',
    illustrationSteps: [
      { roman: 'i.', label: 'Couverture', sub: 'Callebaut 70% dark.' },
      { roman: 'ii.', label: 'Precision melt', sub: 'Controlled heat, never scorched.' },
      { roman: 'iii.', label: 'House ganache', sub: 'Silky, deep, balanced.' },
      { roman: 'iv.', label: 'The fold', sub: 'Finished with steamed milk.' },
    ],
    serviceLabel: 'Served as',
    service: [
      { label: 'Hot Velvet Chocolate', price: '60' },
      { label: 'Iced Velvet Chocolate', price: '65' },
    ],
    footnote: [
      'Oat or almond +15.',
      'Contains dairy, soy lecithin.',
    ],
    // ---- Scenes menu fields ----
    scene: {
      src: '/illustrations/velvet-hero.jpg',
      focus: '62% 50%',
      alt: 'Velvet Chocolate, hot and iced',
      one: 'Callebaut 70 % couverture ganache, folded into steamed milk.',
      priceLine: 'Hot 60 · Iced 65',
    },
    steps: [
      { title: 'Couverture', note: 'Callebaut 70 % dark.' },
      { title: 'Precision melt', note: 'Controlled heat, never scorched.' },
      { title: 'House ganache', note: 'Crafted in-house.' },
      { title: 'The fold', note: 'Into steamed milk.' },
    ],
    serviceRows: [
      { label: 'Hot Velvet Chocolate', price: '60' },
      { label: 'Iced Velvet Chocolate', price: '65' },
    ],
    allergens: 'Oat or almond +15 · Contains dairy, soy lecithin.',
    pagemarkSection: 'SIGNATURES',
    palette: VELVET_PALETTE,
  },

  // -------------------------------------------------------------------
  // ii. CEREMONIAL MATCHA  — deep teal + cream-gold
  // -------------------------------------------------------------------
  {
    slug: 'matcha',
    kind: 'signature-drink',
    folio: '02',
    section: 'I. SIGNATURES · CEREMONIAL MATCHA',
    eyebrow: 'A house signature',
    headline: 'Ceremonial',
    headlineItalic: 'Matcha',
    lede: "Matchamu Kana's first-flush Uji matcha. Stone-ground in Kyoto, sifted at the bar.",
    originMark: { characters: '京都', label: 'UJI · KYOTO' },
    illustrationType: 'hero-single',
    illustrationSrc: '/illustrations/matcha-hero.jpg',
    heroBackdrop: 'matcha-bowl',
    plateNumber: '4.2',
    plateRef: 'A3',
    plateCaption: ['USUCHA · BOWL · OVERHEAD', 'SOFT TOP LIGHT'],
    serviceLabel: 'Service',
    service: [
      { name: 'Usucha', descriptor: 'bowl, whisked', price: '55' },
      { name: 'Latte', descriptor: 'hot', price: '55' },
      { name: 'Latte', descriptor: 'iced', price: '60' },
    ],
    footnote: ['No pre-mixed syrups. Bitter on the first sip is correct.'],
    // ---- Scenes menu fields ----
    scene: {
      src: '/illustrations/matcha-hero-m.jpg',
      focus: '50% 62%',
      alt: 'Iced ceremonial matcha latte',
      one: 'First-flush Uji, stone-ground in Kyoto.',
      priceLine: 'Usucha 55 · Latte 55 · Iced 60',
    },
    steps: [
      { title: 'First flush', note: 'Matchamu Kana, Uji.' },
      { title: 'Stone-ground', note: 'In Kyoto.' },
      { title: 'Sifted', note: 'At the bar, to order.' },
      { title: 'Whisked', note: 'Never pre-mixed.' },
    ],
    serviceRows: [
      { label: 'Usucha', sub: 'bowl, whisked', price: '55' },
      { label: 'Latte', sub: 'hot', price: '55' },
      { label: 'Latte', sub: 'iced', price: '60' },
    ],
    pull: 'No pre-mixed syrups. Bitter on the first sip is correct.',
    pagemarkSection: 'SIGNATURES',
    palette: MATCHA_PALETTE,
  },

  // -------------------------------------------------------------------
  // iii. MONT BLANC  — champagne cream + amber
  // -------------------------------------------------------------------
  {
    slug: 'montblanc',
    kind: 'signature-drink',
    folio: '03',
    section: 'I. SIGNATURES · MONT BLANC',
    eyebrow: 'A house signature',
    headline: 'Mont',
    headlineItalic: 'Blanc',
    lede: 'Aceh Ribang Gayo, chilled over Paragon. Finished with house yuzu cream, orange zest, and dried orange.',
    layoutVariant: 'split-hero',
    illustrationSrc: '/illustrations/montblanc-hero.jpg',
    serviceLabel: 'Served as',
    service: [{ label: 'Mont Blanc', price: '70' }],
    footnote: ['Contains dairy.'],
    // ---- Scenes menu fields ----
    scene: {
      src: '/illustrations/montblanc-hero.jpg',
      focus: '50% 42%',
      alt: 'Mont Blanc — Ribang Gayo over Paragon with yuzu cream',
      one: 'Aceh Ribang Gayo over Paragon, house yuzu cream.',
      priceLine: '70',
    },
    steps: [
      { title: 'Aceh Ribang Gayo', note: 'Brewed, then chilled.' },
      { title: 'Over Paragon', note: 'The chilled drop.' },
      { title: 'House yuzu cream', note: '' },
      { title: 'Orange zest', note: 'And dried orange.' },
    ],
    serviceRows: [{ label: 'Mont Blanc', price: '70' }],
    allergens: 'Contains dairy.',
    pagemarkSection: 'SIGNATURES',
    palette: MONTBLANC_PALETTE,
  },

  { slug: 'back', kind: 'cover-back' },
];

// Cover theme — slipcase + front/back covers.
export const signatureTheme = {
  bookId: 'signatures',
  routeBase: '/signatures',
  coverColor: '#1F140E',
  coverColorAccent: '#14100D',
  accentColor: '#E0B97A',
  bookTitle: 'SIGNATURES',
  frontCover: {
    hero: ['Three', 'drinks,', 'made by hand.'],
    heroAccentLine: 1,
    tag: 'SIGNATURES',
  },
  backCover: {
    hero: ['Salésol Bar.', 'BSD City.'],
    heroAccentLine: 0,
  },
};
